import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  Store, Mail, Phone, MapPin, CreditCard, User, Shield, Clock,
  Check, X, Loader2, Inbox, ChevronDown, ChevronUp, Image as ImageIcon,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PRO_TYPE_LABELS = { shop: "Boutique", association: "Association", organizer: "Organisateur", publisher: "Éditeur" };

function abs(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${API}${url.startsWith("/") ? "" : "/"}${url}`;
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
      <span className="text-muted-foreground min-w-[120px]">{label} :</span>
      <span className="font-medium break-words">{value}</span>
    </div>
  );
}

export default function ShopRequestsPage() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [actionId, setActionId] = useState(null);

  const fetchPending = useCallback(async () => {
    try {
      const res = await api.get("/admin/shops/pending");
      setShops(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const approve = async (shop) => {
    if (!window.confirm(`Approuver la boutique "${shop.name}" ? Le propriétaire passera en rôle ORG.`)) return;
    setActionId(shop.id);
    try {
      await api.post(`/admin/shops/${shop.id}/approve`);
      toast.success(`"${shop.name}" approuvée`);
      setShops((prev) => prev.filter((s) => s.id !== shop.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setActionId(null);
    }
  };

  const reject = async (shop) => {
    const reason = window.prompt(`Rejeter "${shop.name}" ?\nRaison (optionnelle, envoyée au demandeur) :`, "");
    if (reason === null) return;
    setActionId(shop.id);
    try {
      await api.post(`/admin/shops/${shop.id}/reject`, { reason });
      toast.success(`"${shop.name}" rejetée`);
      setShops((prev) => prev.filter((s) => s.id !== shop.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="shop-requests-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="shop-requests-title">
          <Inbox className="w-6 h-6 text-violet-600" /> Demandes PRO
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {shops.length} demande{shops.length > 1 ? "s" : ""} en attente d'approbation
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : shops.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground" data-testid="no-pending-requests">
          <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Aucune demande en attente pour le moment.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {shops.map((shop) => {
            const legal = shop.legal_representative || {};
            const mgr = shop.shop_manager || {};
            const owner = shop.owner_info || {};
            const expanded = expandedId === shop.id;
            const busy = actionId === shop.id;
            return (
              <Card key={shop.id} data-testid={`pending-shop-${shop.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {abs(shop.logo_url) ? (
                        <img src={abs(shop.logo_url)} alt="logo" className="w-12 h-12 rounded-full object-cover border flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0"><Store className="w-5 h-5 text-muted-foreground" /></div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{shop.name}</span>
                          <Badge className="bg-violet-100 text-violet-700 text-xs">{PRO_TYPE_LABELS[shop.pro_type] || "Boutique"}</Badge>
                          <Badge className="bg-amber-100 text-amber-800 text-xs"><Clock className="w-3 h-3 mr-1" />En attente</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Demandé par <span className="font-medium">{owner.pseudo || owner.name || "—"}</span> ({owner.email || "?"})
                        </div>
                        {shop.address && <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{shop.address}</div>}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button size="sm" onClick={() => approve(shop)} disabled={busy} className="bg-green-600 hover:bg-green-700" data-testid={`approve-shop-${shop.id}`}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Approuver
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(shop)} disabled={busy} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-shop-${shop.id}`}>
                        <X className="w-4 h-4 mr-1" />Rejeter
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setExpandedId(expanded ? null : shop.id)} data-testid={`expand-request-${shop.id}`}>
                        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {expanded && (
                    <div className="mt-4 border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                      {shop.description && <p className="text-sm text-muted-foreground md:col-span-2">{shop.description}</p>}
                      <Row icon={Mail} label="Email boutique" value={shop.email} />
                      <Row icon={Phone} label="Téléphone" value={shop.phone} />
                      <Row icon={User} label="Capacité salle" value={shop.room_capacity} />
                      <Row icon={CreditCard} label="IBAN" value={shop.iban} />
                      <div className="md:col-span-2 mt-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1"><Shield className="w-3.5 h-3.5" />Représentant légal</div>
                      <Row icon={User} label="Nom" value={legal.name} />
                      <Row icon={Mail} label="Email" value={legal.email} />
                      <Row icon={Phone} label="Téléphone" value={legal.phone} />
                      {(mgr.name || mgr.email || mgr.phone) && (
                        <>
                          <div className="md:col-span-2 mt-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1"><User className="w-3.5 h-3.5" />Gérant</div>
                          <Row icon={User} label="Nom" value={mgr.name} />
                          <Row icon={Mail} label="Email" value={mgr.email} />
                          <Row icon={Phone} label="Téléphone" value={mgr.phone} />
                        </>
                      )}
                      {abs(shop.banner_url) && (
                        <div className="md:col-span-2 mt-2">
                          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1 mb-1"><ImageIcon className="w-3.5 h-3.5" />Bannière</div>
                          <img src={abs(shop.banner_url)} alt="banner" className="w-full max-w-md rounded-lg border object-cover" />
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
